import React from 'react';
import styled from 'styled-components';

import { useAnchoredPanel } from './useAnchoredPanel';

/**
 * 當日明細：點日期欄頭打開，列出那天想休的人、各自的假別與時段。
 *
 * ⛔ 純唯讀 —— 這個面板裡沒有任何會改資料的按鈕。要改假還是走格子上的
 *    左鍵（一般休・整天）或右鍵（精簡選單），⛔ 不在這裡再開第二條寫入路徑。
 *
 * 「我的順位」是唯讀的一行：後端算好的同一份順位（依職能拆軌、逐月輪替），
 * ⛔ 前端不自己算、⛔ 也不顯示別人的順位 —— 跨軌道的順位數字不能互相比較，
 * 把同事的數字擺在一起只會讓人拿不同軌道的 1 和 2 互相對照。
 * ⛔ 還沒設定任何順位軌道時這一行整個不出現（傳進來的 myRank 是 null）。
 *
 * ⚠️ 面板的定位規則（含捲動時重新定位、Esc 關閉）與 MarkPicker 共用 useAnchoredPanel。
 */
const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1040;
    background: transparent;
`;

const Panel = styled.div`
    position: fixed;
    z-index: 1050;
    width: 300px;
    max-width: calc(100vw - 16px);
    max-height: 62vh;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #cfd6dd;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(31, 41, 51, 0.16);
    padding: 11px 12px;
    font-family: 'Noto Sans TC', sans-serif;

    .detail-head {
        display: flex;
        align-items: flex-start;
        gap: 6px;
    }

    .detail-title {
        flex: 1;
        font-size: 13px;
        font-weight: bold;
        color: #1f2933;
    }

    .detail-close {
        flex-shrink: 0;
        border: 0;
        background: transparent;
        color: #7b8794;
        font-size: 17px;
        line-height: 1;
        padding: 0 2px;
        cursor: pointer;
    }

    .detail-hint {
        font-size: 11px;
        color: #7b8794;
        margin-top: 1px;
        margin-bottom: 8px;
        padding-bottom: 7px;
        border-bottom: 1px solid #eef1f4;
    }

    .detail-rank {
        font-size: 11.5px;
        color: #33475b;
        background: #f3f8fd;
        border: 1px solid #d7e5f4;
        border-radius: 4px;
        padding: 5px 7px;
        margin-bottom: 8px;

        b {
            color: #1864ab;
        }

        .rank-note {
            display: block;
            color: #7b8794;
            font-size: 10.5px;
            margin-top: 2px;
        }
    }

    .detail-empty {
        font-size: 12px;
        color: #7b8794;
        margin: 0 0 4px;
    }

    .detail-foot {
        font-size: 11px;
        color: #9aa5b1;
        margin: 8px 0 0;
        padding-top: 7px;
        border-top: 1px solid #eef1f4;
    }

    table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;
    }

    th,
    td {
        padding: 5px 4px;
        border-bottom: 1px solid #eef1f4;
        text-align: left;
        vertical-align: middle;
        white-space: nowrap;
    }

    thead th {
        font-size: 11px;
        font-weight: bold;
        color: #7b8794;
        border-bottom: 1px solid #dde3e8;
    }

    tbody tr:last-child td {
        border-bottom: 0;
    }

    tbody tr.is-self td {
        background: #f3f8fd;
    }

    .self-tag {
        font-size: 10px;
        background-color: #1864ab;
        color: #fff;
        border-radius: 8px;
        padding: 1px 5px;
        margin-left: 4px;
    }
`;

/** 假別用與月表格子同一組顏色，⛔ 不另外配一套色 */
const CategoryChip = styled.span`
    display: inline-block;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 11px;
    line-height: 1.5;
    color: #fff;
    background-color: ${(props) => props.color || '#90a4ae'};
`;

const DayDetail = ({ anchorSelector, date, rows, myRank, onClose }) => {
    // 'beside'：面板貼在日期欄旁邊，⛔ 不蓋住它正在描述的那一欄
    const { panelRef, pos } = useAnchoredPanel(anchorSelector, onClose, 'beside');

    if (!anchorSelector || !date) return null;

    const list = rows || [];
    const includesSelf = list.some((r) => r.is_self);

    return (
        <React.Fragment>
            <Backdrop
                onClick={onClose}
                onContextMenu={(e) => {
                    // 明細開著時在別處按右鍵：關掉就好，⛔ 不要讓瀏覽器原生選單蓋上來
                    e.preventDefault();
                    onClose();
                }}
            />
            <Panel ref={panelRef} style={pos} data-testid="day-detail">
                <div className="detail-head">
                    <div className="detail-title">
                        {date.date}（{date.weekday}）當日明細
                    </div>
                    <button
                        type="button"
                        className="detail-close"
                        data-action="close-day-detail"
                        aria-label="關閉當日明細"
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>
                <div className="detail-hint">
                    {list.length
                        ? `這天有 ${list.length} 人想休${includesSelf ? '（含你）' : ''}`
                        : '這天目前沒有人劃假'}
                </div>

                {myRank && (
                    <div className="detail-rank">
                        我的順位：
                        {myRank.rank ? (
                            <React.Fragment>
                                {myRank.track_name} <b>第 {myRank.rank} 順位</b>（共 {myRank.total} 人）
                            </React.Fragment>
                        ) : (
                            <b>{myRank.label}</b>
                        )}
                        <span className="rank-note">
                            同一天想休的人太多、名額不夠時，店長依順位取捨。
                            跨軌道的順位不能互相比較。
                        </span>
                    </div>
                )}

                {list.length ? (
                    <table>
                        <thead>
                            <tr>
                                <th>姓名</th>
                                <th>假別</th>
                                <th>時段</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((row) => (
                                <tr key={row.uuid} className={row.is_self ? 'is-self' : ''}>
                                    <td>
                                        {row.name}
                                        {row.is_self && <span className="self-tag">我</span>}
                                    </td>
                                    <td>
                                        <CategoryChip color={row.mark.color}>
                                            {row.mark.category_label}
                                        </CategoryChip>
                                    </td>
                                    <td>{row.mark.period_label}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className="detail-empty">全體同仁這天都是上班。</p>
                )}

                <p className="detail-foot">只看不改：要調整自己的劃假，請在月表自己那一列的格子上操作。</p>
            </Panel>
        </React.Fragment>
    );
};

export default DayDetail;
